import React, { useContext, forwardRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
	AddressInput,
	Checkbox,
	CitySelectInput,
	CompanyNameInput,
	ConfirmPasswordInput,
	EmailInput,
	FirstNameInput,
	LastNameInput,
	PasswordInput,
	PhoneNumberInput,
	PostalCodeInput,
	SelectInput,
	StateSelectInput,
	TextInput,
	useBrandDetails,
} from 'dw-au-react-components';
import useForm from 'react-hook-form';

import { usePostalCodeData, EmotionBreakpoint } from 'dw-au-react-components';
import { BillingForm } from '../BillingForm';
import useEmailValidator from '../../../Hooks/useEmailValidator';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DeliveryInstructionsInput from './DeliveryInstructionsInput';
import { CartContext } from '../../../Context/CartContext';
import { ContentfulContext } from '../../../Context/Contentful';
import { VelocityFrequentFlyerForm } from '../../Forms/VelocityFrequentFlyerForm';
import { VoucherAccordian } from '../../VoucherAccordian';

const StyledAddressForm = styled.form`
	.col-2 {
		flex-basis: 48%;
	}

	.select .dropdown {
		z-index: 9 !important;
	}
	.react-datepicker .react-datepicker__day,
	.react-datepicker .react-datepicker__day-name,
	.react-datepicker .react-datepicker__time-name {
		width: 45px !important;
		line-height: 3rem !important;
		font-size: 14px;
	}

	.select-container.error,
	.input-wrapper.error {
		background: transparent !important;
		border-color: transparent !important;
		padding: 0 !important;
	}

	input[name='email']:disabled {
		background: #f2f2f2;
		cursor: default;
	}

	.label-text,
	.dropdown-text {
		font-weight: normal;
		font-size: 15px;
		color: #212121 !important;
	}

	.react-datepicker-popper {
		z-index: 2;
	}

	.react-datepicker__portal {
		${EmotionBreakpoint('notMobile')} {
			justify-content: flex-end;
			padding-right: 40px;
		}
	}
	.email_input_field .helper-text {
		display: none;
	}
	.helper-text-email {
		font-weight: 300;
		font-size: 13px;
		color: #cf004f;
		letter-spacing: 0.25px;
		padding-left: 12px;
		margin-top: 6px;
		align-items: center;
		display: flex;
	}
	.helper-text-email p {
		font-weight: 300;
		font-size: 13px;
		color: #cf004f;
		letter-spacing: 0.25px;
		align-items: center;
	}
`;

function AddressForm({
	defaultValues = '',
	handleFormSubmission,
	setStep,
	onEmailValidation,
	allowExistingCustomer,
	setGuestShipping,
	setEmailCheck,
}) {
	const {
		register,
		errors,
		handleSubmit,
		watch,
		setValue,
		setError,
		clearError,
		reset,
		unregister,
	} = useForm({
		mode: 'onBlur',
		defaultValues,
	});

	const brandData = useBrandDetails();
	const [selectedValue, setSelectedValue] = useState('');
	const watchedPostalCode = watch('postalCode');
	const watchedCity = watch('city');
	const watchedState = watch('state');
	const [postalCode, setPostalCode] = useState('');
	const [cityList, setCityList] = useState([]);
	const [stateList, setStateList] = useState([]);
	const [addressFormError, setAddressFormError] = useState(false);
	const [emailDisabled, setEmailDisabled] = useState(true);
	const { velocityNumberError } = useContext(CartContext);
	const { showThirdParty, entries } = useContext(ContentfulContext);

	const watchedEmail = watch('email');
	const isEmailValid = useEmailValidator(watchedEmail);

	useEffect(() => {
		if (!allowExistingCustomer) {
			setEmailDisabled(false);
		}
	}, [allowExistingCustomer]);

	useEffect(() => {
		if (isEmailValid) {
			if (!allowExistingCustomer) {
				setValue('email', '');
				setError(
					'email',
					'manual',
					(entries?.items[0]?.fields?.existingUserError || '')
						.replace(/<[^>]*>/g, '')
						.trim() !== ''
						? entries?.items[0]?.fields?.existingUserError
						: 'Sorry, this offer is not available to the existing customers.'
				);
			} else if (onEmailValidation) {
				onEmailValidation(watchedEmail);
				setStep(1);
			}
		}
	}, [isEmailValid]);

	useEffect(() => {
		if (defaultValues) {
			if (defaultValues.billingSameAsShipping === false) {
				setBillingSameAsShipping(defaultValues.billingSameAsShipping);
			}
			setSelectedValue(defaultValues.salutation);
			setValue('salutation', defaultValues.salutation);
		}
	}, [defaultValues]);

	const handleSelectChange = (itemObject) => {
		if (itemObject.value) {
			setSelectedValue(itemObject.value);
			setValue('salutation', itemObject.value);
		}
	};

	useEffect(() => {
		if (watchedPostalCode) {
			setPostalCode(watchedPostalCode);
		}
	}, [watchedPostalCode]);

	let postalCodeData = usePostalCodeData(postalCode, brandData.country);

	const { finalPostalCode, finalCityList, finalStateList, error, isLoading } =
		postalCodeData;

	let invalidPostalCode =
		postalCode.length && error && finalPostalCode === false;

	useEffect(() => {
		if (!isLoading && invalidPostalCode) {
			setError(
				'postalCode',
				'isValidPostalCode',
				'Please enter a valid post code'
			);
			setAddressFormError('Please enter a valid post code');
			setCityList([]);
			setStateList([]);
			setValue('city', '');
			setValue('state', '');
		} else {
			clearError('postalCode');
			setAddressFormError(false);
		}
	}, [invalidPostalCode]);

	useEffect(() => {
		let cityOptions = '';
		if (finalPostalCode && finalCityList.length > 0) {
			cityOptions = finalCityList.map((city) => {
				return {
					option: city,
					value: city,
				};
			});
			setCityList(cityOptions);
			if (cityOptions.length === 1) {
				setValue('city', cityOptions[0].value);
			}
		}
	}, [finalCityList]);

	useEffect(() => {
		let stateOptions = '';
		if (finalPostalCode && finalStateList.length > 0) {
			stateOptions = finalStateList.map((city) => {
				return {
					option: city.stateCode,
					value: city.stateCode,
				};
			});
			setStateList(stateOptions);
			setValue('state', stateOptions[0].value);
			setValue('stateCode', stateOptions[0].value);
		}
	}, [finalStateList]);

	// const watchedEmail = watch('email');
	const passwordInputValue = watch('password');
	const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);

	const [dateOfBirth, setDateOfBirth] = useState('');

	// eslint-disable-next-line react/display-name
	const CustomDOBInput = forwardRef(
		({ onClick, onChange, dateOfBirth = '' }, ref) => {
			return (
				<TextInput
					value={dateOfBirth}
					type='text'
					name='dob'
					label='Date of Birth'
					error={errors?.dob ? true : false}
					onClick={onClick}
					onChange={onChange}
					autoComplete='off'
					register={register({ required: 'Missing Date of Birth' })}
					maxLength={10}
					helperText={errors?.dob ? 'Missing DOB' : 'Required'}
					readOnly={true}
				/>
			);
		}
	);

	const updateDOB = (date) => {
		let d = new Date(date),
			datestring =
				('0' + d.getDate()).slice(-2) +
				'/' +
				('0' + (d.getMonth() + 1)).slice(-2) +
				'/' +
				d.getFullYear();
		if (datestring) {
			setDateOfBirth(datestring);
			setValue('dob', datestring);
			clearError('dob');
		}
	};

	const cancelCta = (
		<div
			className='btn btn-outline-secondary btn-block'
			onClick={() => {
				reset({});
				if (allowExistingCustomer) {
					setGuestShipping(false);
					// setEmailCheck(null);
				} else {
					setStep(0);
				}
			}}
		>
			Back
		</div>
	);

	return (
		<StyledAddressForm
			className='m-0'
			onSubmit={handleSubmit((data) => handleFormSubmission(data, 2))}
		>
			<div className='mb-16 email_input_field'>
				<EmailInput
					register={register}
					errors={errors}
					disabled={emailDisabled}
				/>
				{errors?.email?.message && (
					<div
						className='helper-text-email'
						dangerouslySetInnerHTML={{
							__html: errors?.email?.message,
						}}
					/>
				)}
			</div>

			<div className='mb-16 d-none'>
				<SelectInput
					optionsList={[
						{
							option: 'Mr.',
							value: 'Mr.',
						},
						{
							option: 'Mrs.',
							value: 'Mrs.',
						},
						{
							option: 'Ms.',
							value: 'Ms.',
						},
						{
							option: 'Miss',
							value: 'Miss',
						},
						{
							option: 'Dr',
							value: 'Dr',
						},
						{
							option: 'Prof',
							value: 'Prof',
						},
					]}
					label='Title'
					register={register({ name: 'salutation' })}
					errors={errors}
					onChange={handleSelectChange}
					value={selectedValue}
				/>
			</div>

			<div className='mb-16'>
				<FirstNameInput
					register={register}
					errors={errors}
					label='First Name'
					placeholder='First Name'
				/>
			</div>

			<div className='mb-16'>
				<LastNameInput register={register} errors={errors} />
			</div>

			<div className='mb-16 d-none'>
				<CompanyNameInput register={register} />
			</div>

			<div className='mb-16'>
				<PhoneNumberInput
					register={register}
					errors={errors}
					country={brandData.country}
				/>
			</div>

			<DatePicker
				register={register({ name: 'dob', required: 'Missing DOB' })}
				customInput={<CustomDOBInput dateOfBirth={dateOfBirth} />}
				onChange={(date) => {
					updateDOB(date);
				}}
				dateFormat='dd/MM/yyyy'
				maxDate={new Date().setFullYear(new Date().getFullYear() - 18)}
				minDate={new Date().setFullYear(new Date().getFullYear() - 100)}
				showMonthDropdown
				showYearDropdown
				dropdownMode='select'
				onKeyDown={(e) => {
					e.preventDefault();
				}}
				style={{ width: '300px' }}
				withPortal
			/>

			{/* <div className='mb-16'>
				<div className='subtitle mb-8 lower roboto'>
					Create account password
				</div>
				<div className='text-dark-medium'>
					To redeem this offer please create an account. Your password must be a
					minimum of 8 characters and contain at least 1 number and 1 letter.
				</div>
			</div> */}

			<div className='mb-16 subtitle lower roboto'>Shipping information</div>

			<div className='mb-16'>
				<AddressInput
					register={register}
					errors={errors}
					country={brandData.country}
				/>
			</div>

			{brandData.country !== 'NZ' && (
				<div className='mb-16'>
					<AddressInput
						type={2}
						required={false}
						register={register}
						errors={errors}
						country={brandData.country}
					/>
				</div>
			)}

			<div className='mb-16'>
				<PostalCodeInput register={register} errors={errors} />
			</div>

			<div
				className={`${
					brandData.country !== 'NZ' ? 'd-flex justify-content-between' : ''
				}`}
			>
				<div className={`mb-16 ${brandData.country !== 'NZ' ? 'col-2' : ''}`}>
					<div className='section form-row'>
						<CitySelectInput
							register={register}
							errors={errors}
							cityList={cityList}
							setValue={setValue}
							clearError={clearError}
							defaultValue={watchedCity}
						/>
					</div>
				</div>

				<div className={`mb-16 ${brandData.country !== 'NZ' ? 'col-2' : ''}`}>
					{brandData.country !== 'NZ' ? (
						<div className='section form-row'>
							<StateSelectInput
								register={register}
								errors={errors}
								stateList={stateList}
								setValue={setValue}
								clearError={clearError}
								defaultValue={watchedState}
							/>
							<input
								type='hidden'
								name='stateCode'
								/* eslint-disable react/no-unknown-property */
								register={register({ name: 'stateCode' })}
							/>
						</div>
					) : null}
				</div>
			</div>

			<div className='mb-16'>
				<DeliveryInstructionsInput
					defaultValue={
						defaultValues.deliveryInstructionsOtherInput
							? defaultValues.deliveryInstructionsOtherInput
							: defaultValues.deliveryInstructions
					}
					register={register}
					errors={errors}
					setValue={setValue}
					clearError={clearError}
					unregister={unregister}
				/>
			</div>

			<div className='mb-18'>
				<Checkbox
					label='Billing address is same as shipping address'
					name='billingSameAsShipping'
					register={register}
					checked={billingSameAsShipping}
					onChange={() => setBillingSameAsShipping(!billingSameAsShipping)}
				/>
			</div>

			<BillingForm
				register={register}
				watch={watch}
				setError={setError}
				clearError={clearError}
				setValue={setValue}
				errors={errors}
				billingSameAsShipping={billingSameAsShipping}
				brandData={brandData}
				defaultValues={defaultValues}
			/>

			<div className='mb-16'>
				<PasswordInput register={register} errors={errors} isNewPassword />
			</div>
			<div className='mb-16'>
				<ConfirmPasswordInput
					register={register}
					errors={errors}
					passwordInputValue={passwordInputValue}
				/>
			</div>

			<div className='mb-16 text-dark-medium'>
				Your password must be a minimum of 8 characters and contain at least 1
				number and 1 letter.
			</div>

			{showThirdParty && (
				<VoucherAccordian title='Velocity Membership Number'>
					<VelocityFrequentFlyerForm />
				</VoucherAccordian>
			)}

			<button
				type='submit'
				disabled={velocityNumberError}
				className={
					'btn btn-primary font-weight-bold dmlp__btn w-100 mb-20 dmlp_shipping_submit_button'
				}
			>
				Save & Continue
			</button>
			{cancelCta}

			{addressFormError && (
				<div className='mt-20 alert alert-danger'>{addressFormError}</div>
			)}
		</StyledAddressForm>
	);
}

export default AddressForm;
